import { PageWrapper } from '@components'
import {
  LABELS,
  LIFECYCLE_STATUS_LABELS,
  QUOTATION_STATUS_LABELS,
  ROUTE_PARTS,
  TOOLTIPS,
  URL_PATHS,
  WARNINGS,
} from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Add, Close, Download, FilterList, Settings } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material'
import {
  GridRow,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { OvDataGrid } from 'src/components/core/ov-data-grid/ov-data-grid'
import { POLICIES } from 'src/constants/policies.constants'
import { MobileDocumentRow } from 'src/partials/index.partials'

export const QuotationListView = observer(() => {
  const { quotation, policies, ui } = useStore()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [selectedYear, setSelectedYear] = useState<number | ''>('')
  const [selectedQuarter, setSelectedQuarter] = useState<number | ''>('')
  const [selectedLifecycleStatus, setSelectedLifecycleStatus] = useState<string>('')
  const [selectedQuotationStatus, setSelectedQuotationStatus] = useState<string>('')
  const [selectedQuotations, setSelectedQuotations] = useState<GridRowSelectionModel>([])
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const handleRowClick = (params: GridRowParams) => {
    if (!params.id) return
    const path = generatePath(URL_PATHS.QUOTE_DETAIL, { id: params.id })
    return navigate(path)
  }

  const handleNewClick = async () => {
    navigate(URL_PATHS.QUOTE_CREATE)
  }

  const canCreate = useMemo(() => {
    return policies.can(POLICIES.DOCUMENT.CREATE)
  }, [policies.current_policies])

  // Generate year options (current year and 5 years back)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => currentYear - i)
  }, [])

  // Filter params to pass to data grid
  const filterParams = useMemo(() => {
    const params: Record<string, any> = {}
    if (selectedYear) params.year = selectedYear
    if (selectedQuarter) params.quarter = selectedQuarter
    if (selectedLifecycleStatus) params.lifecycle_status = selectedLifecycleStatus
    if (selectedQuotationStatus) params.quotation_status = selectedQuotationStatus
    return params
  }, [selectedYear, selectedQuarter, selectedLifecycleStatus, selectedQuotationStatus])

  // Count all active filters (any filter that has a value set)
  const activeFilterCount = useMemo(() => {
    let count = 0
    // Count year filter if selected
    if (selectedYear !== '') count++
    // Count quarter filter if selected
    if (selectedQuarter !== '') count++
    // Count lifecycle status if selected
    if (selectedLifecycleStatus) count++
    // Count quotation status if selected
    if (selectedQuotationStatus) count++
    return count
  }, [selectedYear, selectedQuarter, selectedLifecycleStatus, selectedQuotationStatus])

  const handleClearFilters = () => {
    setSelectedYear('')
    setSelectedQuarter('')
    setSelectedLifecycleStatus('')
    setSelectedQuotationStatus('')
    setFilterModalOpen(false)
  }

  const canDownloadSelectedQuotations = useMemo(
    () => quotation.canBulkDownload(selectedQuotations as number[]),
    [selectedQuotations, quotation.data_grid.current_rows]
  )

  const handleBulkDownload = async () => {
    try {
      await quotation.downloadBulkPdf(selectedQuotations as number[])
      ui.handleToaster(t('success:quotations_downloading'), { type: 'success' })
    } catch (e: any) {
      ui.handleToaster(e.message || t('errors:download_failed'), { type: 'error' })
    }
  }

  const CustomRow = useCallback(
    (props: any) =>
      isMobile ? (
        <MobileDocumentRow
          {...props.row}
          type="quotation"
          onRowClick={handleRowClick}
        />
      ) : (
        <GridRow {...props} />
      ),
    [isMobile]
  )

  const CustomToolbar = useCallback(() => {
    return (
      <GridToolbarContainer
        sx={({ palette }) => ({
          p: 2,
          borderBottom: '2px solid',
          borderBottomColor: palette.divider,
          gap: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
        })}>
        <GridToolbarQuickFilter
          variant="outlined"
          size="small"
          placeholder={LABELS.SEARCH}
          sx={{
            minWidth: isMobile ? '100%' : 200,
          }}
        />
        {!isMobile && <GridToolbarDensitySelector />}
        <Box sx={{ flexGrow: 1 }} />
        {isMobile && activeFilterCount > 0 && (
          <Tooltip title={TOOLTIPS.CLEAR_ALL_FILTERS}>
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              sx={{ mr: 1 }}>
              Filters wissen ({activeFilterCount})
            </Button>
          </Tooltip>
        )}
        {isMobile && (
          <Tooltip title={TOOLTIPS.OPEN_FILTERS}>
            <Button
              size="small"
              variant="text"
              onClick={() => setFilterModalOpen(true)}
              endIcon={
                <Badge
                  badgeContent={activeFilterCount}
                  color="error"
                  invisible={activeFilterCount === 0}>
                  <FilterList />
                </Badge>
              }>
              Filters
            </Button>
          </Tooltip>
        )}
        {!isMobile && (
          <>
            <FormControl
              size="small"
              sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedLifecycleStatus}
                label="Status"
                onChange={e => setSelectedLifecycleStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(LIFECYCLE_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 150 }}>
              <InputLabel>Offertestatus</InputLabel>
              <Select
                value={selectedQuotationStatus}
                label="Offertestatus"
                onChange={e => setSelectedQuotationStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(QUOTATION_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 120 }}>
              <InputLabel>Jaar</InputLabel>
              <Select
                value={selectedYear}
                label="Jaar"
                onChange={e => setSelectedYear(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle jaren</em>
                </MenuItem>
                {yearOptions.map(year => (
                  <MenuItem
                    key={year}
                    value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 120 }}>
              <InputLabel>Kwartaal</InputLabel>
              <Select
                value={selectedQuarter}
                label="Kwartaal"
                onChange={e => setSelectedQuarter(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle kwartalen</em>
                </MenuItem>
                <MenuItem value={1}>Q1 (Jan-Mrt)</MenuItem>
                <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                <MenuItem value={4}>Q4 (Okt-Dec)</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
        {canDownloadSelectedQuotations && (
          <Tooltip title={TOOLTIPS.BULK_DOWNLOAD_QUOTATIONS}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              endIcon={<Download />}
              onClick={handleBulkDownload}>
              Download {selectedQuotations.length} {selectedQuotations.length === 1 ? 'offerte' : 'offertes'}
            </Button>
          </Tooltip>
        )}
      </GridToolbarContainer>
    )
  }, [
    selectedYear,
    selectedQuarter,
    selectedLifecycleStatus,
    selectedQuotationStatus,
    yearOptions,
    selectedQuotations,
    isMobile,
    handleBulkDownload,
    canDownloadSelectedQuotations,
    activeFilterCount,
  ])

  return (
    <>
      <PageWrapper
        pageTitle={LABELS.QUOTES}
        allowed={policies.can(POLICIES.DOCUMENT.READ)}
        allowedMessage={WARNINGS.NOT_ALLOWED.NO_COMPANY}
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'space-between' : 'flex-end',
            }}>
            <Tooltip title={TOOLTIPS.OPEN_QUOTATION_SETTINGS}>
              <Box sx={{ flex: isMobile ? 1 : 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canCreate}
                  onClick={() =>
                    navigate(`/${URL_PATHS.QUOTE_SETTINGS}#${ROUTE_PARTS.QUOTE_LIST}`, { state: { from: 'quotes' } })
                  }
                  endIcon={<Settings />}
                  sx={{ width: isMobile ? '100%' : 'auto' }}>
                  {LABELS.SETTINGS}
                </Button>
              </Box>
            </Tooltip>
            <Tooltip title={TOOLTIPS.CREATE_NEW_QUOTATION}>
              <Box sx={{ flex: isMobile ? 1 : 0 }}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!canCreate}
                  endIcon={<Add />}
                  onClick={handleNewClick}
                  sx={{ width: isMobile ? '100%' : 'auto' }}>
                  Nieuw
                </Button>
              </Box>
            </Tooltip>
          </Box>
        }>
        <OvDataGrid
          slots={{
            row: CustomRow,
            toolbar: CustomToolbar,
          }}
          withToolBar={true}
          onRowClick={handleRowClick}
          store={quotation.data_grid}
          responsiveColumns
          params={filterParams}
          checkboxSelection={!isMobile}
          onRowSelectionModelChange={selection => setSelectedQuotations(selection)}
          rowSelectionModel={selectedQuotations}
          initialState={{
            sorting: {
              sortModel: [{ field: 'issued_at', sort: 'desc' }],
            },
          }}
        />
      </PageWrapper>

      {/* Mobile Filter Modal */}
      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          Filters
          <IconButton
            onClick={() => setFilterModalOpen(false)}
            size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedLifecycleStatus}
                label="Status"
                onChange={e => setSelectedLifecycleStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(LIFECYCLE_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Offertestatus</InputLabel>
              <Select
                value={selectedQuotationStatus}
                label="Offertestatus"
                onChange={e => setSelectedQuotationStatus(e.target.value)}>
                <MenuItem value="">
                  <em>Alle statussen</em>
                </MenuItem>
                {Object.entries(QUOTATION_STATUS_LABELS).map(([value, label]) => (
                  <MenuItem
                    key={value}
                    value={value}>
                    {String(label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Jaar</InputLabel>
              <Select
                value={selectedYear}
                label="Jaar"
                onChange={e => setSelectedYear(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle jaren</em>
                </MenuItem>
                {yearOptions.map(year => (
                  <MenuItem
                    key={year}
                    value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              size="small">
              <InputLabel>Kwartaal</InputLabel>
              <Select
                value={selectedQuarter}
                label="Kwartaal"
                onChange={e => setSelectedQuarter(e.target.value as number)}>
                <MenuItem value="">
                  <em>Alle kwartalen</em>
                </MenuItem>
                <MenuItem value={1}>Q1 (Jan-Mrt)</MenuItem>
                <MenuItem value={2}>Q2 (Apr-Jun)</MenuItem>
                <MenuItem value={3}>Q3 (Jul-Sep)</MenuItem>
                <MenuItem value={4}>Q4 (Okt-Dec)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Tooltip title={TOOLTIPS.CLEAR_ALL_FILTERS}>
            <Button
              onClick={handleClearFilters}
              variant="outlined"
              fullWidth>
              Alle filters wissen
            </Button>
          </Tooltip>
          <Tooltip title={TOOLTIPS.APPLY_FILTERS}>
            <Button
              onClick={() => setFilterModalOpen(false)}
              variant="contained"
              fullWidth>
              Toepassen
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  )
})
